<template>
  <header class="htop">
    <div class="htop__left">
      <!-- unter 768 px: Navigation als Schublade (öffnet die Shell) -->
      <button
        v-if="mobile"
        type="button"
        class="htop__nav"
        aria-label="Navigation öffnen"
        :aria-expanded="navOpen ? 'true' : 'false'"
        @click="$emit('toggle-nav')"
      >
        <app-icon name="menu" :size="16" />
      </button>
      <img src="/img/sima-star.png" alt="" width="22" height="22" class="htop__star" />
      <template v-if="!mobile">
        <span class="htop__slash" aria-hidden="true">/</span>
        <app-hub-tenant class="htop__tenant" />
      </template>
      <app-hub-breadcrumb class="htop__crumb" :compact="mobile" />
    </div>

    <div class="htop__right">
      <app-hub-search />
      <app-theme-toggle v-if="themeToggle" />
      <app-hub-status v-if="!mobile" />
      <app-icon-button
        v-if="phoneAction"
        :icon="phoneAction.icon"
        :label="phoneAction.label"
        @click="runPhoneAction"
      />
      <app-hub-user-menu @sign-out="$emit('sign-out')" />
    </div>
  </header>
</template>

<script>
// Kopfleiste der Hub-Shell (Mobility-Hub-Analyse „topbar“ und „mobile“, Farben aus tokens.css): fest oben über die
// volle Breite, also über Navigation und Inhalt (die Navigation beginnt darunter), Höhe var(--hub-top) = 48 (Touch 56,
// setzt die Shell an .shell--hub), z-index 30 (unter der Notch mit 40), --surface (hell weiß) mit Linie --line unten,
// kein Schatten.
// Polster 0 20 0 16, Flex mit space-between und Abstand 8; Inhalte auf der Mitte.
// Links (Flex, Abstand 10, darf schrumpfen): unter 768 px (Prop mobile) der Navigationsknopf 28 × 28 (randlos, Icon
// menu 16, Radius --r-ctl, Hover --hov; Touch 44 × 44, die Icon-Achse bleibt), Ereignis toggle-nav, aria-expanded aus
// $shell.hubNavOpen · SIMA-Stern 22 (kein Link) · ab 768 px „/“ 16/24 --ink-off und der Mandant (AppHubTenant) ·
// Brotkrumen (AppHubBreadcrumb; unter 768 px nur der Seitentitel ohne „/“).
// Notch: ab 640 px (darunter blendet AppHubNotch sich aus) ist der linke Teil höchstens calc(50 % − 130px) breit und
// endet rund 20 px vor der Notch (sichtbar 224 breit, mittig am Fenster). Bei Platzmangel hat der Mandant Vorrang:
// die Brotkrumen nehmen nur den Rest (erst entfällt die Gruppe, dann kürzt der Seitentitel), danach kürzt der Name.
// Rechts (Flex, Abstand 8): Suche (AppHubSearch) · der Knopf „Darstellung“ (Farbschema und Hauptfarbe) (AppThemeToggle, wie in der
// Systemleiste links neben dem Verbindungsstatus; Kundenwunsch 12.10.2026, docs/redesign/umsetzung.md, Nachtrag
// 12.10.2026 Nr. 15) erst ab 1040 px, mit Touch-Dichte ab 1080 px: Er macht die rechte Gruppe 40 (Touch 52) breiter, und
// schmaler schöbe er die Suchpille an oder unter die Notch, auch wenn der Status „Keine Verbindung“ zeigt (38 breiter als
// „Verbunden“). Ab diesen Breiten bleiben zwischen Notch und Pille verbunden rund 45 px, getrennt rund 9; darunter steht
// die Leiste wie ohne Schalter · ab 768 px der Status (AppHubStatus) · auf dem Handy
// (unter 600 px, $dichte.handy, und nicht bei offenem Mehr-Menü) die Seitenaktion aus $shell.phoneAction als
// Icon-Knopf 32/44 – nur dort, weil die Seiten ihre eigenen Aktionen nur auf dem Handy ausblenden (sonst stünde sie
// zwischen 600 und 767 px doppelt da) · Benutzermenü (AppHubUserMenu, Ereignis sign-out wird weitergereicht, die Shell
// meldet ab).
// Safe Area (viewport-fit=cover): links und rechts im Polster; oben wächst die Leiste um env(safe-area-inset-top)
// (installierte App), der Inhalt beginnt dann bei calc(var(--hub-top) + env(safe-area-inset-top, 0px)).
// Druck: steht nur oben auf der ersten Seite statt auf jeder.
// Prop mobile (Boolean, unter 768 px). Ereignisse toggle-nav und sign-out. Keine Slots.
import AppHubTenant from './AppHubTenant.vue';
import AppHubBreadcrumb from './AppHubBreadcrumb.vue';
import AppHubSearch from './AppHubSearch.vue';
import AppHubStatus from './AppHubStatus.vue';
import AppHubUserMenu from './AppHubUserMenu.vue';
import AppThemeToggle from '../shell/AppThemeToggle.vue';

// Mindestbreite (Fenster, CSS-px) für den Knopf „Darstellung“, gemessen in Chromium (macOS-Systemschrift) mit
// Suchpille 192, Status-Chip und Benutzerknopf: kleinster Abstand zwischen Notch-Kontur und Pille dort verbunden rund 45,
// bei „Keine Verbindung“ rund 9 px (siehe oben)
const THEME_TOGGLE_MIN = { maus: 1040, touch: 1080 };

export default {
  name: 'AppHubTopbar',
  components: { AppHubTenant, AppHubBreadcrumb, AppHubSearch, AppHubStatus, AppHubUserMenu, AppThemeToggle },
  props: {
    // unter 768 px: Navigationsknopf statt „/“ und Mandant, Brotkrumen nur mit dem Seitentitel, ohne Status
    mobile: { type: Boolean, default: false }
  },
  computed: {
    navOpen() {
      return !!(this.$shell && this.$shell.hubNavOpen);
    },
    // Knopf „Darstellung“ nur, wo er neben der Notch Platz hat
    themeToggle() {
      if (this.mobile) {
        return false;
      }
      const touch = !!(this.$dichte && this.$dichte.touch);
      const width = this.$vuetify && this.$vuetify.breakpoint ? this.$vuetify.breakpoint.width : 0;
      return width >= (touch ? THEME_TOGGLE_MIN.touch : THEME_TOGGLE_MIN.maus);
    },
    // höchstens eine Seitenaktion ({ icon, label, handler }) wie in der Handy-App-Leiste
    phoneAction() {
      const shell = this.$shell;
      const handy = !!(this.$dichte && this.$dichte.handy);
      const action = this.mobile && handy && shell && !shell.moreOpen ? shell.phoneAction : null;
      return action && action.icon && action.label ? action : null;
    }
  },
  methods: {
    runPhoneAction() {
      const action = this.phoneAction;
      if (action && typeof action.handler === 'function') {
        action.handler();
      }
    }
  }
};
</script>

<style scoped>
.htop {
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  z-index: 30;
  height: calc(var(--hub-top, 48px) + env(safe-area-inset-top, 0px));
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: env(safe-area-inset-top, 0px) calc(20px + env(safe-area-inset-right, 0px)) 0 calc(16px + env(safe-area-inset-left, 0px));
  background: var(--surface);
  border-bottom: 1px solid var(--line);
  color: var(--ink);
  font-family: var(--font);
}
@media print {
  .htop {
    position: absolute;
  }
}

/* ---------------------------------------------------------------- links */
.htop__left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 1 auto;
  min-width: 0;
}
/* Notch sichtbar (ab 640): 50 % der Inhaltsbreite minus 130 endet 20 px vor ihrer sichtbaren Kante */
@media (min-width: 640px) {
  .htop__left {
    max-width: calc(50% - 130px);
  }
}
/* Navigationsknopf randlos wie im Hub (ghost, 28) */
.htop__nav {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: var(--r-ctl);
  background: transparent;
  color: var(--ink);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
:where(html:not(.dichte-touch)) .htop__nav:hover {
  background: var(--hov);
}
.htop__nav:active {
  background: var(--press);
}
.htop__nav:focus {
  outline: none;
}
.htop__nav:focus-visible {
  outline: 2px solid var(--link);
  outline-offset: 2px;
}
/* Touch: Tippfläche 44, je 8 px nach außen versetzt – Icon und Stern bleiben, wo sie mit der Maus stehen */
html.dichte-touch .htop__nav {
  width: 44px;
  height: 44px;
  margin: 0 -8px;
}
.htop__star {
  display: block;
  flex: none;
  width: 22px;
  height: 22px;
}
.htop__slash {
  flex: none;
  font-size: 16px;
  line-height: 24px;
  color: var(--ink-off);
  -webkit-user-select: none;
  user-select: none;
}
/* Platzmangel: der Mandant hat Vorrang und schrumpft nicht, ist aber höchstens so breit wie der linke Teil ohne Stern,
   Trenner und Abstände (zusammen rund 57, Puffer für breitere Schrägstriche anderer Systemschriften); erst dort kürzt
   der Name. Die Brotkrumen nehmen den Rest: erst entfällt die Gruppe, dann kürzt der Seitentitel, unter 44 px ist er
   unsichtbar (er steht ohnehin im Seitenkopf). */
.htop__tenant {
  flex: 0 0 auto;
  min-width: 0;
  max-width: calc(100% - 60px);
}
.htop__crumb {
  flex: 0 1 auto;
  min-width: 0;
}

/* ---------------------------------------------------------------- rechts */
.htop__right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: none;
}
/* Knopf „Darstellung“ im Stil von Suche und Status dieser Leiste (Mobility Hub): Kante --line ohne Schatten
   statt der Knopfkante im Blatt, auch bei Hover und beim Drücken. Die Flächen (Hover --hov, gedrückt --press) und der
   eingeschaltete Zustand (--sel mit Kante --link, aria-pressed) kommen unverändert aus AppThemeToggle bzw. .app-ib.
   Mit .htop stärker als die Zustandsregeln von .app-ib (bedienelemente.css wird nach den Komponenten geladen). */
.htop .htop__right ::v-deep .theme-toggle {
  box-shadow: none;
}
.htop .htop__right ::v-deep .theme-toggle:not(.app-ib--pressed) {
  border-color: var(--line);
}
</style>
